import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SortField, SortOrder } from "@/components/candidates/CandidatesViewOptions";
import { toast } from "@/hooks/use-toast";
import { Company } from "@/components/CompanyCard";

const ITEMS_PER_PAGE = 25;

export interface CompanyFilters {
	searchTerm?: string;
	assignedToMe?: boolean;
	currentUserId?: string | null;
	showFavoritesOnly?: boolean;
	favorites?: Set<string>;
	relationshipFilter?: string[];
}

export interface UseCompaniesPaginatedReturn {
	companies: Company[];
	isLoading: boolean;
	totalCount: number;
	totalPages: number;
	currentPage: number;
	setCurrentPage: (page: number) => void;
	refetch: () => Promise<void>;
}

export const useCompaniesPaginated = (
	filters: CompanyFilters,
	sortField: SortField = "dateCreated",
	sortOrder: SortOrder = "desc"
): UseCompaniesPaginatedReturn => {
	const [companies, setCompanies] = useState<Company[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [totalCount, setTotalCount] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [orgUuid, setOrgUuid] = useState<string | null>(null);

	// Fetch organization UUID once on mount
	useEffect(() => {
		const fetchOrgUuid = async () => {
			try {
				const { data: { user } } = await supabase.auth.getUser();
				if (!user) return;

				const { data: userOrg } = await supabase
					.from('user_organization')
					.select('organization_uuid')
					.eq('user_uuid', user.id)
					.single();

				if (userOrg?.organization_uuid) {
					setOrgUuid(userOrg.organization_uuid);
				}
			} catch (error) {
				console.error("Error fetching organization:", error);
			}
		};

		fetchOrgUuid();
	}, []);

	const fetchCompanies = useCallback(async () => {
		if (!orgUuid) {
			// Keep loading state true while waiting for orgUuid
			return;
		}

		try {
			setIsLoading(true);

			// Start building query - APPLY ALL FILTERS IN DATABASE
			let query = supabase
				.from('companies')
				.select('*', { count: 'exact' })
				.eq('organization_uuid', orgUuid);

			// Assigned to me filter
			if (filters.assignedToMe && filters.currentUserId) {
				query = query.eq('assignee', filters.currentUserId);
			}

			// Search term filter (searches existing companies columns only)
			if (filters.searchTerm && filters.searchTerm.trim() !== "") {
				const term = filters.searchTerm.trim();
				query = query.or(`name.ilike.%${term}%,website_url.ilike.%${term}%,vat_number.ilike.%${term}%,city.ilike.%${term}%`);
			}

			// Relationship filter (multi-select)
			if (filters.relationshipFilter && filters.relationshipFilter.length > 0) {
				query = query.in('relationship', filters.relationshipFilter);
			}

			// Sorting
			const sortMapping: Record<SortField, string> = {
				name: 'name',
				dateCreated: 'created_at',
				matchScore: 'id' // Companies don't have match_score, fallback to id
			};
			const dbSortField = sortMapping[sortField] || 'created_at';
			query = query.order(dbSortField, { ascending: sortOrder === 'asc' });

			// Pagination - AFTER all filters
			const from = (currentPage - 1) * ITEMS_PER_PAGE;
			query = query.range(from, from + ITEMS_PER_PAGE - 1);

			const { data, error, count } = await query;

			if (error) {
				throw error;
			}

			// Client-side favorites filter (if favorites are not in database)
			let finalData = (data || []) as Company[];
			if (filters.showFavoritesOnly && filters.favorites && filters.favorites.size > 0) {
				finalData = finalData.filter(c => filters.favorites!.has(c.id));
			}

			setCompanies(finalData);
			setTotalCount(count || 0);
		} catch (error) {
			console.error("Error fetching companies:", error);
			toast({
				title: "Failed to load companies",
				description: error instanceof Error ? error.message : "Unknown error",
				variant: "destructive",
			});
			setCompanies([]);
			setTotalCount(0);
		} finally {
			setIsLoading(false);
		}
	}, [
		orgUuid,
		currentPage,
		filters.searchTerm,
		filters.assignedToMe,
		filters.currentUserId,
		filters.showFavoritesOnly,
		filters.favorites,
		filters.relationshipFilter,
		sortField,
		sortOrder
	]);

	useEffect(() => {
		fetchCompanies();
	}, [fetchCompanies]);

	// Reset to page 1 when filters change
	useEffect(() => {
		setCurrentPage(1);
	}, [
		filters.searchTerm,
		filters.assignedToMe,
		filters.showFavoritesOnly,
		filters.relationshipFilter
	]);

	const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

	return {
		companies,
		isLoading,
		totalCount,
		totalPages,
		currentPage,
		setCurrentPage,
		refetch: fetchCompanies
	};
};
